import { useTranslation } from "react-i18next";
import { currencySymbol } from "../../../../data/deliveryTariffs";

/**
 * The home-delivery tariff of a warehouse: one row per weight band, transport
 * per kg in the warehouse's currency (Tbilisi / regions, highland) and the
 * courier fee in lari per zone. State lives in the page — this only draws it
 * and reports edits through onChange(idx, key, value), onAdd and onRemove, so
 * saving works exactly as before.
 */

// Number field without spinner arrows, unit shown inside on the right.
const Cell = ({ value, onChange, unit, placeholder, step = "0.01", invalid, title, tone }) => (
  <div className="relative">
    <input
      type="number"
      step={step}
      value={value}
      placeholder={placeholder}
      title={title}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full min-w-[84px] h-9 pl-2.5 pr-7 rounded-lg text-right text-sm font-semibold tabular-nums
        border bg-white text-slate-800 placeholder:text-slate-300 placeholder:font-normal
        focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400
        dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-600
        [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none
        ${invalid
          ? "border-red-400 ring-1 ring-red-300 dark:border-red-500"
          : tone === "transport"
            ? "border-teal-200 dark:border-teal-800"
            : "border-indigo-200 dark:border-indigo-800"}`}
    />
    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 pointer-events-none">
      {unit}
    </span>
  </div>
);

const TariffEditor = ({ brackets, currency, onChange, onAdd, onRemove }) => {
  const { t } = useTranslation();
  const sym = currencySymbol(currency);
  const rows = brackets ?? [];

  // A band must start where the previous one ended and end after it starts —
  // a gap or overlap prices some weight from the wrong row.
  const rangeIssue = (br, idx) => {
    const from = Number(br.from_kg ?? 0);
    const to = Number(br.to_kg ?? 0);
    if (!(to > from)) return t("admin_tariff_bad_range") || "„მდე“ უნდა იყოს „დან“-ზე მეტი";
    if (idx > 0 && from !== Number(rows[idx - 1].to_kg ?? 0)) {
      return (t("admin_tariff_gap") || "უნდა დაიწყოს წინა რიგის ბოლოდან") + ` (${rows[idx - 1].to_kg})`;
    }
    return null;
  };

  const head = "px-2 py-2 font-semibold text-[11px] uppercase tracking-wide whitespace-nowrap";

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="text-slate-500 dark:text-slate-400">
              <th rowSpan={2} className={`${head} text-left bg-slate-50 dark:bg-slate-800 align-bottom`}>
                {t("admin_bracket_range")}
              </th>
              <th colSpan={2} className={`${head} text-center bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 border-l border-slate-200 dark:border-slate-700`}>
                {t("admin_tariff_transport") || "ტრანსპორტირება"} · {sym}/კგ
              </th>
              <th colSpan={3} className={`${head} text-center bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border-l border-slate-200 dark:border-slate-700`}>
                {t("admin_tariff_courier") || "საკურიერო"} · ₾
              </th>
              <th rowSpan={2} className="bg-slate-50 dark:bg-slate-800 w-10" />
            </tr>
            <tr className="text-slate-500 dark:text-slate-400">
              <th className={`${head} text-right bg-teal-50/60 dark:bg-teal-900/20 border-l border-slate-200 dark:border-slate-700 normal-case`}>
                {t("tbilisi_and_regions") || "თბილისი / რეგიონები"}
              </th>
              <th className={`${head} text-right bg-teal-50/60 dark:bg-teal-900/20 normal-case`}>
                {t("highland") || "მაღალმთიანი"}
              </th>
              <th className={`${head} text-right bg-indigo-50/60 dark:bg-indigo-900/20 border-l border-slate-200 dark:border-slate-700 normal-case`}>
                {t("tbilisi") || "თბილისი"}
              </th>
              <th className={`${head} text-right bg-indigo-50/60 dark:bg-indigo-900/20 normal-case`}>
                {t("region") || "რეგიონი"}
              </th>
              <th className={`${head} text-right bg-indigo-50/60 dark:bg-indigo-900/20 normal-case`}>
                {t("highland") || "მაღალმთიანი"}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((br, idx) => {
              const issue = rangeIssue(br, idx);
              const fees = [br.delivery_tbilisi_gel, br.delivery_region_gel ?? br.delivery_tbilisi_gel, br.delivery_highland_gel];
              const included = fees.every((f) => Number(f ?? 0) === 0);
              return (
                <tr
                  key={idx}
                  className="border-t border-slate-100 dark:border-slate-700/70 odd:bg-white even:bg-slate-50/70
                             dark:odd:bg-slate-800 dark:even:bg-slate-800/60 hover:bg-teal-50/40 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <td className="px-2 py-2 align-top">
                    <div className="flex items-center gap-1.5">
                      <div className="w-[72px]">
                        <Cell value={br.from_kg ?? 0} step="0.1" unit="" tone="transport" invalid={!!issue}
                              title={issue || ""} onChange={(v) => onChange(idx, "from_kg", v)} />
                      </div>
                      <span className="text-slate-400">–</span>
                      <div className="w-[72px]">
                        <Cell value={br.to_kg ?? 0} step="0.1" unit="" tone="transport" invalid={!!issue}
                              title={issue || ""} onChange={(v) => onChange(idx, "to_kg", v)} />
                      </div>
                      <span className="text-[11px] text-slate-400">კგ</span>
                    </div>
                    {issue && <p className="mt-1 text-[10px] text-red-500 leading-tight">{issue}</p>}
                  </td>
                  <td className="px-2 py-2 align-top border-l border-slate-100 dark:border-slate-700/70">
                    <Cell value={br.transport_per_kg ?? 0} unit={sym} tone="transport"
                          onChange={(v) => onChange(idx, "transport_per_kg", v)} />
                  </td>
                  <td className="px-2 py-2 align-top">
                    {/* Empty = the Tbilisi rate, as the server reads it. */}
                    <Cell value={br.transport_per_kg_highland ?? ""} unit={sym} tone="transport"
                          placeholder={String(br.transport_per_kg ?? "")}
                          title={t("admin_tariff_highland_empty") || "ცარიელი = თბილისის ფასი"}
                          onChange={(v) => onChange(idx, "transport_per_kg_highland", v)} />
                  </td>
                  <td className="px-2 py-2 align-top border-l border-slate-100 dark:border-slate-700/70">
                    <Cell value={br.delivery_tbilisi_gel ?? 0} unit="₾" tone="courier"
                          onChange={(v) => onChange(idx, "delivery_tbilisi_gel", v)} />
                  </td>
                  <td className="px-2 py-2 align-top">
                    <Cell value={br.delivery_region_gel ?? br.delivery_tbilisi_gel ?? 0} unit="₾" tone="courier"
                          onChange={(v) => onChange(idx, "delivery_region_gel", v)} />
                  </td>
                  <td className="px-2 py-2 align-top">
                    <Cell value={br.delivery_highland_gel ?? 0} unit="₾" tone="courier"
                          onChange={(v) => onChange(idx, "delivery_highland_gel", v)} />
                    {included && (
                      <p className="mt-1 text-[10px] text-indigo-500 dark:text-indigo-300 text-right leading-tight">
                        {t("admin_tariff_courier_included") || "საკურიერო შედის ტრანსპორტირებაში"}
                      </p>
                    )}
                  </td>
                  <td className="px-1 py-2 align-top text-center">
                    <button
                      type="button"
                      onClick={() => onRemove(idx)}
                      title={t("delete") || "წაშლა"}
                      className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50
                                 dark:hover:bg-red-900/30 flex items-center justify-center text-lg leading-none transition-colors"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400 bg-white dark:bg-slate-800">
                  {t("admin_no_brackets_yet") || "ტარიფი ცარიელია — დაამატე რიგი ქვემოთ"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={onAdd}
          className="text-xs font-semibold rounded-lg bg-teal_600 text-white px-4 py-2 hover:opacity-90 shadow-sm"
        >
          + {t("admin_add_row") || "დაამატე რიგი"}
        </button>
        <p className="text-[11px] text-slate-400">
          {rows.length} {t("admin_tariff_rows") || "რიგი"} ·{" "}
          {t("admin_tariff_footer") ||
            "ზოლს ირჩევს ერთი მიმღების ერთი რეისის ჯამური წონა; საკურიერო — ერთ მისამართზე გასატანი ამანათების ჯამური წონა."}
        </p>
      </div>
    </div>
  );
};

export default TariffEditor;
